import React, { useState, useEffect, useCallback, useContext } from 'react';
import MainLayout from '../../components/layouts/MainLayout';
import {
  Box, Typography, Card, CardContent, Grid, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Chip, IconButton,
  Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Select, InputLabel, FormControl, Switch, FormControlLabel,
  CircularProgress, Snackbar, Alert, Tabs, Tab,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { CONTEXTS } from 'figshelf-modules';

const ASSET_TYPES = [
  { value: 'avatar_frame', label: 'Avatar Frame' },
  { value: 'color_palette', label: 'Color Palette' },
  { value: 'shelf_design', label: 'Shelf Design' },
  { value: 'app_icon', label: 'App Icon' },
];

const EMPTY_FORM = {
  name: '', description: '', type: 'avatar_frame',
  previewUrl: '', assetUrl: '', isActive: true, sortOrder: 0,
  config: '',
};

const AssetFormDialog = ({ open, asset, onClose, onSave }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (asset) {
      setForm({
        name: asset.name || '',
        description: asset.description || '',
        type: asset.type || 'avatar_frame',
        previewUrl: asset.previewUrl || '',
        assetUrl: asset.assetUrl || '',
        isActive: asset.isActive !== false,
        sortOrder: asset.sortOrder || 0,
        config: asset.config ? JSON.stringify(asset.config, null, 2) : '',
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [asset, open]);

  const handleSubmit = async () => {
    setSaving(true);
    let config = null;
    if (form.config.trim()) {
      try { config = JSON.parse(form.config); } catch { config = null; }
    }
    await onSave({ ...form, config });
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{asset ? 'Edit Asset' : 'New Asset'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        <TextField label="Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} fullWidth required />
        <TextField label="Description" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} fullWidth multiline rows={2} />
        <FormControl fullWidth>
          <InputLabel>Type</InputLabel>
          <Select value={form.type} label="Type" onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}>
            {ASSET_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField label="Preview URL" value={form.previewUrl} onChange={(e) => setForm((f) => ({ ...f, previewUrl: e.target.value }))} fullWidth />
        <TextField label="Asset URL" value={form.assetUrl} onChange={(e) => setForm((f) => ({ ...f, assetUrl: e.target.value }))} fullWidth />
        <TextField label="Sort Order" type="number" value={form.sortOrder} onChange={(e) => setForm((f) => ({ ...f, sortOrder: Number(e.target.value) }))} fullWidth />
        <TextField label="Config (JSON)" value={form.config} onChange={(e) => setForm((f) => ({ ...f, config: e.target.value }))} fullWidth multiline rows={3} placeholder='e.g. {"primary": "#FF5722"}' />
        <FormControlLabel control={<Switch checked={form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} />} label="Active" />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving || !form.name}>
          {saving ? <CircularProgress size={18} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const PremiumFeatures = () => {
  const api = useContext(CONTEXTS.APIContext);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editAsset, setEditAsset] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const activeType = ASSET_TYPES[tabIndex]?.value;

  const fetchAssets = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/premium/assets/admin');
      const raw = res?.data ?? res ?? [];
      setAssets(Array.isArray(raw) ? raw : []);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load assets', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchAssets(); }, [fetchAssets]);

  const visibleAssets = assets.filter((a) => a.type === activeType);

  const handleSave = async (form) => {
    try {
      if (editAsset) {
        await api.patch(`/premium/assets/admin/${editAsset.id}`, form);
        setSnackbar({ open: true, message: 'Asset updated', severity: 'success' });
      } else {
        await api.post('/premium/assets/admin', form);
        setSnackbar({ open: true, message: 'Asset created', severity: 'success' });
      }
      setDialogOpen(false);
      setEditAsset(null);
      fetchAssets();
    } catch {
      setSnackbar({ open: true, message: 'Save failed', severity: 'error' });
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/premium/assets/admin/${id}`);
      setSnackbar({ open: true, message: 'Asset deleted', severity: 'success' });
      setDeleteConfirm(null);
      fetchAssets();
    } catch {
      setSnackbar({ open: true, message: 'Delete failed', severity: 'error' });
    }
  };

  return (
    <MainLayout>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Typography variant="h5" fontWeight={600}>Premium Assets</Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Manage avatar frames, colour palettes, shelf designs and app icons
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditAsset(null); setDialogOpen(true); }}>
            New Asset
          </Button>
        </Box>

        <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} sx={{ mb: 2 }}>
          {ASSET_TYPES.map((t) => <Tab key={t.value} label={t.label} />)}
        </Tabs>

        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 0 }}>
            {loading ? (
              <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Preview</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Sort</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleAssets.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                            No assets yet. Click "New Asset" to create one.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {visibleAssets.map((asset) => (
                      <TableRow key={asset.id}>
                        <TableCell>
                          {asset.previewUrl
                            ? <Box component="img" src={asset.previewUrl} alt={asset.name} sx={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 1, bgcolor: 'action.hover' }} />
                            : <Box sx={{ width: 48, height: 48, bgcolor: 'action.hover', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><WorkspacePremiumIcon fontSize="small" color="disabled" /></Box>
                          }
                        </TableCell>
                        <TableCell><Typography variant="body2" fontWeight={600}>{asset.name}</Typography></TableCell>
                        <TableCell><Typography variant="body2" color="text.secondary">{asset.description || '—'}</Typography></TableCell>
                        <TableCell>{asset.sortOrder}</TableCell>
                        <TableCell>
                          <Chip label={asset.isActive ? 'Active' : 'Inactive'} color={asset.isActive ? 'success' : 'default'} size="small" />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => { setEditAsset(asset); setDialogOpen(true); }}><EditIcon fontSize="small" /></IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => setDeleteConfirm(asset)}><DeleteIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>

      <AssetFormDialog
        open={dialogOpen}
        asset={editAsset}
        onClose={() => { setDialogOpen(false); setEditAsset(null); }}
        onSave={handleSave}
      />

      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Asset</DialogTitle>
        <DialogContent>
          <Typography>Delete <b>{deleteConfirm?.name}</b>? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => handleDelete(deleteConfirm.id)}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </MainLayout>
  );
};

export default PremiumFeatures;